import React, { ReactNode, useMemo } from 'react';
import styled from 'styled-components';

import { getStargateScannerTxUrl } from 'components/balances/bridge/utils';
import { getChainIcon } from 'components/balances/chains/chainIcons/chainIcons';
import LabelClickable from 'components/basic/Button/LabelClickable';
import CloseIcon from 'components/basic/icons/CloseIcon';
import Link from 'components/basic/Link/Link';
import { useConfirmationModal } from 'components/basic/modal/ConfirmationModal';
import CircleProgressBar from 'components/basic/progress/CircleProgreessBar';
import { StargateExplorerDoubleIcon } from 'components/wallet/icons/explorer/StargateExplorerDoubleIcon';
import config from 'config';
import { getBlockExplorerTransactionUrl } from 'config/configUtils';
import { KATANA_PERPS } from 'config/constants';
import { hoursDifferenceLongerThan } from 'lib/date';
import { removePendingDeposit } from 'reduxState/balances/pendingDeposits';
import { usePendingDepositsConfirmations } from 'reduxState/balances/pendingDepositsUtils';
import { staticDispatch, useSelector } from 'reduxState/store';

import type { PendingDepositItem } from 'reduxState/balances/pendingDepositTypes';

const StatusLogo = styled.img`
  height: 12px;
  margin-left: 4px;
  margin-right: 4px;
`;

export const useRemoveInfoAboutFailedDepositConfirm = ({
  txHash,
}: {
  txHash: string;
}) => {
  const [placeholder, show] = useConfirmationModal({
    title: 'Deposit',
    message: 'Remove information about failed deposit ?',
    confirmButtonProps: {
      autoFocus: true,
    },
    onConfirm: () => {
      console.log('Removing pending deposit', txHash);
      staticDispatch(removePendingDeposit(txHash));
    },
  });
  return {
    removeInfoFailedDepositPlaceholder: placeholder,
    showRemoveInfoFailedDepositConfirmation: show,
  };
};

function RemoveIcon({ txHash }: { txHash: string }) {
  const {
    removeInfoFailedDepositPlaceholder,
    showRemoveInfoFailedDepositConfirmation,
  } = useRemoveInfoAboutFailedDepositConfirm({
    txHash,
  });
  return (
    <>
      {' '}
      <CloseIcon onClick={showRemoveInfoFailedDepositConfirmation} />
      {removeInfoFailedDepositPlaceholder}
    </>
  );
}

function FailedStatus({ txHash }: { txHash: string }) {
  return (
    <>
      <LabelClickable type="error" margin="0 0 0 4px">
        Failed
      </LabelClickable>
      <RemoveIcon txHash={txHash} />
    </>
  );
}

function LinkExtended({
  href,
  disabled,
  disabledLook,
  children,
  margin,
}: {
  href: string;
  disabled: boolean;
  disabledLook?: boolean;
  children: ReactNode;
  margin?: string;
}) {
  return (
    <Link
      href={href}
      disabled={disabled}
      style={disabled || disabledLook ? { opacity: 0.3, margin } : { margin }}
    >
      {children}
    </Link>
  );
}

/** Optional pending count - only if confirmation count > 1 */
function PendingCount({
  blockConfirmationDelay,
  confirmationCount,
  confirmationRate,
}: {
  blockConfirmationDelay: number;
  confirmationCount: number;
  confirmationRate: number | undefined;
}) {
  const isMobile = useSelector(state => state.screen.isMobile);
  if (!confirmationRate) {
    return null;
  }
  return (
    <>
      {!isMobile && blockConfirmationDelay && blockConfirmationDelay > 1 && (
        <>
          ({Math.min(confirmationCount || 0, blockConfirmationDelay)}/
          {blockConfirmationDelay}
          )&nbsp;&nbsp;
        </>
      )}
      <CircleProgressBar rate={confirmationRate} size={15} />
    </>
  );
}

export function PendingStatus({
  transaction,
}: {
  transaction: PendingDepositItem;
}) {
  const isMobile = useSelector(state => state.screen.isMobile);
  const { getTransactionConfirmations, blockConfirmationDelay } =
    usePendingDepositsConfirmations();

  const { sourceChain, bridgeTxId, sourceTxId, bridge } = transaction;

  const confirmations = getTransactionConfirmations(sourceTxId);
  const confirmationsBridged = getTransactionConfirmations(bridgeTxId);

  const sourceTransactionLink = getBlockExplorerTransactionUrl(
    sourceTxId,
    config.chains[sourceChain]
  );

  const isPendingLongTime = useMemo(
    () =>
      transaction.type === 'pendingDeposit' &&
      hoursDifferenceLongerThan(
        transaction.time,
        // Safe to ignore - does not need to be accurate
        // eslint-disable-next-line react-hooks/purity
        Date.now(),
        24 * 2 /* 2 days */
      ),
    [transaction.type, transaction.time]
  );

  if (!bridge) {
    return (
      <>
        {!isMobile && (
          <>
            <Link href={sourceTransactionLink}>
              <StatusLogo src={getChainIcon(sourceChain)} />
            </Link>
          </>
        )}

        {confirmations.confirmationCount < 0 ? (
          <FailedStatus txHash={sourceTxId} />
        ) : (
          <>
            <LabelClickable margin="0 0 0 4px">
              Depositing to {KATANA_PERPS}{' '}
              <PendingCount
                blockConfirmationDelay={blockConfirmationDelay}
                confirmationCount={confirmations.confirmationCount}
                confirmationRate={confirmations.confirmationRate}
              />
            </LabelClickable>
            {isPendingLongTime && <RemoveIcon txHash={sourceTxId} />}
          </>
        )}
      </>
    );
  }

  const statusText = (
    <>
      {confirmations.confirmationCount < 0 ||
      confirmationsBridged.confirmationCount < 0 ? (
        <FailedStatus txHash={sourceTxId} />
      ) : (
        <>
          <LabelClickable margin="0 0 0 4px">
            {bridgeTxId ? (
              <>
                Depositing to {KATANA_PERPS}{' '}
                <PendingCount
                  blockConfirmationDelay={blockConfirmationDelay}
                  confirmationCount={confirmationsBridged.confirmationCount}
                  confirmationRate={confirmations.confirmationRate}
                />
              </>
            ) : confirmations.confirmationCount > 0 ? (
              'Bridging to Katana'
            ) : (
              'Transfering to bridge'
            )}
          </LabelClickable>
          {isPendingLongTime && <RemoveIcon txHash={sourceTxId} />}
        </>
      )}
    </>
  );

  if (isMobile) {
    return statusText;
  }

  return (
    <>
      <LinkExtended href={sourceTransactionLink} disabled={false}>
        <StatusLogo src={getChainIcon(sourceChain)} />
      </LinkExtended>
      {bridge === 'Stargate' && (
        <>
          <LinkExtended
            href={
              getStargateScannerTxUrl(transaction.sourceTxId)
              // transaction.bridgeMessageNonce
              //   ? getStargateScannerTxUrl(transaction.sourceTxId)
              //   : getStargateScannerUrl()
            }
            margin="0 4px"
            disabled={false}
            // disabledLook={!transaction.bridgeMessageNonce}
          >
            <StargateExplorerDoubleIcon
              chain={sourceChain}
              chainType="source"
              size={12}
              inlineFlex
            />
          </LinkExtended>
          {transaction.sourceChain !==
            config.chains.katanaChain.stargateJumpChain && (
            <LinkExtended
              href={
                transaction.forwarderTxId
                  ? getStargateScannerTxUrl(transaction.forwarderTxId)
                  : '#'
              }
              margin="0 4px"
              disabled={!transaction.forwarderTxId}
              // disabledLook={!transaction.bridgeMessageNonce}
            >
              <StargateExplorerDoubleIcon
                chain="ethereum"
                chainType="source"
                size={12}
                inlineFlex
              />
            </LinkExtended>
          )}
        </>
      )}
      <LinkExtended href={'#'} disabled>
        <StatusLogo src={getChainIcon('katanaChain')} />
      </LinkExtended>

      {isMobile && <div style={{ padding: 4 }} />}

      {statusText}
    </>
  );
}
